import { useState, type FormEvent, type JSX } from 'react';
import { Link } from 'react-router-dom';
import { AuthError, isValidEmail } from '@yuan-math/auth';
import { Button, Card } from '@yuan-math/ui';
import { useAuth } from '../state/AuthContext.tsx';
import { AccountsUnavailable } from './RegisterPage.tsx';

/**
 * Requesting a password reset.
 *
 * The confirmation is deliberately identical whether or not the address has an
 * account. Saying "no account with that email" would let anyone test which
 * addresses are registered here — and this is the one page that can be used
 * without credentials.
 */
export function ForgotPasswordPage(): JSX.Element {
  const { requestPasswordReset, configured } = useAuth();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!configured) return <AccountsUnavailable />;

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);

    if (!isValidEmail(email)) {
      setError('That does not look like an email address.');
      return;
    }

    setBusy(true);
    try {
      await requestPasswordReset(email);
      setSent(true);
    } catch (cause) {
      // Only genuine failures — rate limiting, mainly — reach here.
      setError(cause instanceof AuthError ? cause.message : 'Could not send a reset link just now.');
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="page page--narrow">
        <h1 className="page__title">Check your email</h1>
        <p className="page__lead">
          If <strong>{email}</strong> has an account, a link to set a new password is on its way.
        </p>
        <Card>
          <p>
            The link lasts one hour and works once. Nothing has changed yet — your current password still works
            until you set a new one.
          </p>
        </Card>
        <p className="page__note">
          Nothing arrived? Check the spam folder, then try again — the address may be different from the one you
          registered with.
        </p>
        <div className="button-row">
          <Link to="/signin" className="btn btn--secondary">Back to sign in</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page page--narrow">
      <h1 className="page__title">Reset your password</h1>
      <p className="page__lead">
        Tell us the address you registered with and we will send a link to set a new password.
      </p>

      <form className="start-form" onSubmit={submit} noValidate>
        <label className="field">
          <span className="field__label">Email</span>
          <input
            className="field__input"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="email"
            autoFocus
          />
          {error ? <span className="field__error">{error}</span> : null}
        </label>

        <Button type="submit" size="lg" disabled={busy || email.trim() === ''}>
          {busy ? 'Sending…' : 'Send reset link'}
        </Button>

        <p className="field__hint">
          Remembered it? <Link to="/signin">Sign in</Link>.
        </p>
      </form>
    </div>
  );
}
